import React, { Fragment } from 'react';
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    ListGroup,
    ListGroupItem,
    Modal,
    NavLink,
    Row,
} from 'reactstrap';
import Scrollbar from 'react-perfect-scrollbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons/faTrashAlt';
import cx from 'classnames';
import { history } from 'store';
import { notificationStatus } from 'common/enums';
import { LayoutTypes } from 'store/layout';
import { uiPages, scenarioReportQueueType, layoutNotificationTypes } from 'store/layout/types';

interface EventsListModalProps {
    actions: {
        clean: () => void;
        dismiss: (processUid: string) => void;
    };
    events: LayoutTypes.DashboardStatus[];
    isOpen: boolean;
    onClose: () => void;
}

const statuses: Record<number, { color: string; text: string }> = {
    [notificationStatus.pending]: { color: 'info', text: 'Pending' },
    [notificationStatus.completed]: { color: 'success', text: 'Completed' },
    [notificationStatus.none]: { color: 'secondary', text: 'None' },
    [notificationStatus.error]: { color: 'danger', text: 'Error' },
    [notificationStatus.executing]: { color: 'warning', text: 'Executing' },
};

const getRunningJobTitle = (runMode: string) => {
    switch (runMode) {
        case 'RunAll':
            return 'Running PSO';
        case 'LoadPartition':
            return 'Loading Cube';
        case 'DeletePartition':
            return 'Loading Cube';
        case 'BatchRead':
            return 'Running Validation';
        default:
            return `Running job '${runMode}'`;
    }
};

const DashboardLoadItemTemplate: React.FC<{ event: LayoutTypes.DashboardLoadingStatus }> = ({ event }) => (
    <div className="d-flex flex-column">
        <b className="text-primary">{`Loading Dashboard ${event.dashboard}`}</b>
        <span className="mr-2">
            <b className="mr-auto">Status message: </b> {event.status || 'N/A'}
        </span>
        <div className="d-block">
            <span className="mr-2">
                <b className="mr-auto">% Complete: </b> {event.progress}
            </span>
        </div>
    </div>
);

const RunningPSOJobsTemplate: React.FC<{ event: LayoutTypes.RunningPSOJobStatus; onClose: () => void; }> = ({ event, onClose }) => {
    const { payload } = event;

    const redirect = () => {
        if (payload.scenarioId == null) {
            return;
        }

        history.push(`/status/${payload.scenarioId}`);
        onClose();
    };

    return (
        <div>
            <Row>
                <Col className="w-100 mb-1">
                    <b className="text-primary mr-auto">{getRunningJobTitle(payload.runMode)}</b>
                </Col>
            </Row>
            <Row>
                <Col sm="5" className="d-flex flex-column">
                    <span>
                        <b className="mr-auto">Project: </b>
                        <span className="text-primary">#{payload.projectId} {payload.projectName}</span>
                    </span>
                    <span>
                        <b className="mr-auto">Task: </b>
                        <span className="text-primary">#{payload.taskId} {payload.taskName}</span>
                    </span>
                    {payload.scenarioName && payload.scenarioId && (
                        <span>
                            <b className="mr-auto"> Case: </b>
                            <NavLink to={`/status/${payload.scenarioId}`} onClick={redirect} className="d-inline-block p-0">
                                <Button color="link" className="p-0">
                                    <span className="text-primary">#{payload.scenarioId} {payload.scenarioName}</span>
                                </Button>
                            </NavLink>
                        </span>
                    )}
                </Col>
                <Col sm="5" className="d-flex flex-column">
                    <span>
                        <b className="mr-auto">Run status : </b>
                        <span>{payload.runStatus}</span>
                    </span>
                </Col>
            </Row>
        </div>
    );
};

const CopyFilesItemTemplate: React.FC<{ event: LayoutTypes.CopyFilesStatus }> = ({ event }) => (
    <Fragment>
        <div>
            <span className="text-primary mr-auto">{`Copying files. ${event.requestName}`}</span>
            <span className="ml-2">
                <div className="d-block">
                    <span className="mr-2">
                        <b className="mr-auto">Complete: </b>{`${event.progress}%`}
                    </span>
                </div>
            </span>
        </div>
    </Fragment>
);

const CopyTaskItemTemplate: React.FC<{ event: LayoutTypes.CopyTaskStatus }> = ({ event }) => (
    <Fragment>
        <div>
            <span className="text-primary mr-auto">{`Copying task ${event.taskId}.`}</span>
            <span className="ml-2">
                <div className="d-block">
                    <span className="mr-2">
                        <b className="mr-auto">Status message: </b> {event.statusMessage}
                    </span>
                </div>
                <div className="d-block">
                    <span className="mr-2">
                        <b className="mr-auto">Complete: </b>{`${Number(event.progress) * 100}%`}
                    </span>
                </div>
            </span>
        </div>
    </Fragment>
);

const RunningFunctionsTemplate: React.FC<{ event: LayoutTypes.RunningFunctionsStatus }> = ({ event }) => (
    <div className="d-flex flex-column">
        <b className="text-primary">{`Running function '${event.functionName}'`}</b>
        <span className="mr-2">
            <b className="mr-auto">{event.type === uiPages.scenarioGroup ? 'Case groups' : 'Cases'}: </b>
            <span className="text-primary">{event.names.join(', ')}</span>
        </span>
        <span className="mr-2">
            <b className="mr-auto">Status message: </b> {event.status || 'N/A'}
        </span>
    </div>
);

const RunningJobsTemplate: React.FC<{
    event: LayoutTypes.RunningJobStatus;
    onClose: () => void;
}> = ({ event, onClose }) => {
    const { payload } = event;

    const redirect = () => {
        if (payload.scenarioId == null) {
            return;
        }

        history.push(`/status/${payload.scenarioId}`);
        onClose();
    };

    return (
        <div>
            <Row>
                <Col className="w-100 mb-1">
                    <b className="text-primary mr-auto">{getRunningJobTitle(payload.runMode)}</b>
                </Col>
            </Row>
            <Row>
                <Col sm="5" className="d-flex flex-column">
                    <span>
                        <b className="mr-auto">Project: </b>
                        <span className="text-primary">#{payload.projectId} {payload.projectName}</span>
                    </span>
                    <span>
                        <b className="mr-auto">Task: </b>
                        <span className="text-primary">#{payload.taskId} {payload.taskName}</span>
                    </span>
                    {payload.scenarioName && payload.scenarioId && (
                        <span>
                            <b className="mr-auto"> Case: </b>
                            <NavLink to={`/status/${payload.scenarioId}`} onClick={redirect} className="d-inline-block p-0">
                                <Button color="link" className="p-0">
                                    <span className="text-primary">#{payload.scenarioId} {payload.scenarioName}</span>
                                </Button>
                            </NavLink>
                        </span>
                    )}
                    {payload.segmentIdx != null && (
                        <span>
                            <b className="mr-auto">Segment: </b>
                            <span className="text-primary">{payload.segmentIdx}</span>
                        </span>
                    )}
                </Col>
                <Col sm="5" className="d-flex flex-column">
                    <span>
                        <b className="mr-auto">% Complete : </b>
                        <span>{payload.progress}</span>
                    </span>
                    <span>
                        <b className="mr-auto">Run status : </b>
                        <span>{payload.runStatus}</span>
                    </span>
                    {payload.startWork && (
                        <span>
                            <b className="mr-auto">Start work : </b>
                            <span> {dayjs(payload.startWork).format('MM/DD/YYYY HH:mm:ss')}</span>
                        </span>
                    )}
                    {payload.endWork && (
                        <span>
                            <b className="mr-auto">End work : </b>
                            <span>{dayjs(payload.endWork).format('MM/DD/YYYY HH:mm:ss')}</span>
                        </span>
                    )}
                </Col>
            </Row>
            {payload.statusMessage && (
                <Row className="mt-2">
                    <Col>
                        <b>Status message : </b>{' '}
                        <span className={cx({ 'text-danger': event.processStatus === notificationStatus.error })}>
                            {payload.statusMessage}
                        </span>
                    </Col>
                </Row>
            )}
        </div>
    );
};

const RunningReportsTemplate: React.FC<{ event: LayoutTypes.RunningReportsStatus }> = ({ event }) => (
    <div className="d-flex flex-column">
        <b className="text-primary">
            {event.type === scenarioReportQueueType.load
                ? `Loading cases into '${event.athenaDbName}'`
                : `Unloading cases from '${event.athenaDbName}'`}
        </b>
        <span className="mr-2">
            <b className="mr-auto">Project: </b>
            <span className="text-primary">#{event.projectId} {event.projectName}</span>
        </span>
        <span className="mr-2">
            <b className="mr-auto">Cases: </b>
            <span className="text-primary">{event.scenarioNames.join(', ')}</span>
        </span>
    </div>
);

const RunningStaticReportTemplate: React.FC<{ event: LayoutTypes.RunningStaticReportStatus }> = ({ event }) => (
    <div className="d-flex flex-column">
        <b className="text-primary">{event.requestName}</b>
        <span className="mr-2">
            <b className="mr-auto">Progress: </b>
            <span className="text-primary">{`${Number(event.progress) * 100}%`}</span>
        </span>
        <span className="mr-2">
            <b className="mr-auto">Status message: </b> {event.statusMessage || 'N/A'}
        </span>
    </div>
);

const renderTemplate = (event: LayoutTypes.DashboardStatus, onClose: () => void) => {
    switch (event.notificationType) {
        case layoutNotificationTypes.dashboardLoading:
            return <DashboardLoadItemTemplate event={event} />;
        case layoutNotificationTypes.runningJobs:
            return <RunningJobsTemplate event={event} onClose={onClose} />;
        case layoutNotificationTypes.copyFiles:
            return <CopyFilesItemTemplate event={event} />;
        case layoutNotificationTypes.runningPSOJobs:
            return <RunningPSOJobsTemplate event={event} onClose={onClose} />;
        case layoutNotificationTypes.runningFunctions:
            return <RunningFunctionsTemplate event={event} />;
        case layoutNotificationTypes.runningReports:
            return <RunningReportsTemplate event={event} />;
        case layoutNotificationTypes.copyTask:
            return <CopyTaskItemTemplate event={event} />;
        case layoutNotificationTypes.runningStaticReportQueries:
        case layoutNotificationTypes.runningPSOLogs:
            return <RunningStaticReportTemplate event={event} />;
        default:
            return null;
    }
};

const renderStatus = (status: number) => {
    const { color, text } = statuses[status] || {};
    return <Badge color={color}>{text || 'Invalid status'}</Badge>;
};

const EventsListModal: React.FC<EventsListModalProps> = ({ actions, events, isOpen, onClose }) => (
    <Fragment>
        <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
            <Card className="card-box overflow-hidden">
                <div className="card-header bg-light">
                    <div className="card-header--title">
                        <b>Events list</b>
                    </div>
                    <div className="card-header--actions" />
                </div>
                {events.length > 0 && (
                    <Scrollbar style={{ maxHeight: '65vh' }}>
                        <ListGroup flush>
                            {events.map((event, index) => (
                                <ListGroupItem key={event.processUid || index} className="w-100">
                                    <Row>
                                        <Col sm="8">{renderTemplate(event, onClose)}</Col>
                                        <Col sm="4">
                                            <div className="align-box-row">
                                                <div className="flex-column">
                                                    <span className="align-box-row">
                                                        <b className="mr-auto">Process status: </b>
                                                        <span className="ml-2">{renderStatus(event.processStatus)}</span>
                                                    </span>
                                                    <span className="align-box-row">
                                                        <b className="mr-auto">Latest update: </b>
                                                        <span className="ml-2">
                                                            {dayjs(event.eventTime).format('MM/DD/YYYY HH:mm:ss')}
                                                        </span>
                                                    </span>
                                                    <span className="align-box-row">
                                                        <b className="mr-auto">Run by: </b>
                                                        <span className="ml-2">{event.initiatorName}</span>
                                                    </span>
                                                </div>
                                                {notificationStatus.pending !== event.processStatus &&
                                                    notificationStatus.executing !== event.processStatus && (
                                                        <div className="ml-auto mr-3">
                                                            <FontAwesomeIcon
                                                                style={{ cursor: 'pointer' }}
                                                                icon={faTrashAlt}
                                                                onClick={() => actions.dismiss(event.processUid)}
                                                                size="lg"
                                                            />
                                                        </div>
                                                    )}
                                            </div>
                                        </Col>
                                    </Row>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </Scrollbar>
                )}
                {events.length === 0 && (
                    <CardBody>
                        <p className="text-center">No events yet</p>
                    </CardBody>
                )}
                <div className="justify-content-end p-2">
                    <Button color="link" onClick={onClose}>Close</Button>
                </div>
            </Card>
        </Modal>
    </Fragment>
);

export default EventsListModal;
